import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { SelfRecorder } from '../../components/contents/self-recorder';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { Mic, Send, Volume2 } from 'lucide-react';
import { Button } from '../../../core/button';
import { AudioSettingsPopover, useAudioSettings } from '../../../custom/audio-settings';

interface RecordedExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

export function RecordedExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: RecordedExerciseProps) {
  const initialRecordingUrl = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.Recorded) {
      return submission.recordingUrl ?? '';
    }
    return '';
  }, [content.lastSubmission]);

  const [recordingUrl, setRecordingUrl] = useState(initialRecordingUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Audio device selection
  const { selectedMicId, setSelectedMicId, getMicConstraints } = useAudioSettings();

  useEffect(() => {
    setRecordingUrl(initialRecordingUrl);
  }, [content.id, initialRecordingUrl]);

  const handleRecordingUploaded = useCallback((url: string) => {
    setRecordingUrl(url);
  }, []);

  const handleRecordingCleared = useCallback(() => {
    setRecordingUrl('');
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.Recorded,
        recordingUrl,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return secs === 0 ? `${mins}min` : `${mins}min ${secs}s`;
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-6',
        variant === 'card' && 'rounded-[24px] border border-rose-100 bg-white/90 p-6 shadow-sm backdrop-blur'
      )}
    >
      {/* Header with title and audio settings */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {content.title && (
            <h3 className="text-xl font-semibold text-gray-900">
              {renderInlineMarkdown(content.title, 'recorded-exercise-title')}
            </h3>
          )}
        </div>
        {!isReadOnly && (
          <AudioSettingsPopover
            selectedMicId={selectedMicId}
            onMicSelect={setSelectedMicId}
            showSpeakerSelection={false}
          />
        )}
      </div>
      
      <ContentDescription text={content.description} markdown />
      
      {/* Info badge */}
      {/* <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-rose-50 to-pink-50 px-4 py-2 border border-rose-100">
          <Mic className="h-4 w-4 text-[#cf2e2e]" />
          <span className="text-sm text-gray-700 font-medium">Voice Recording</span>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1.5 border border-gray-200">
          <Clock className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-xs text-gray-600">Max: {formatDuration(content.maxDurationSec ?? 60)}</span>
        </div>
      </div> */}

      {isReadOnly ? (
        <div className="rounded-[20px] border border-rose-100 bg-gradient-to-b from-white to-rose-50/30 p-5">
          {recordingUrl ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#cf2e2e] to-[#f78da7]">
                  <Volume2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Recorded Answer</p>
                  <p className="text-xs text-gray-500">Submitted recording</p>
                </div>
              </div>
              <audio 
                controls 
                src={recordingUrl} 
                className="w-full rounded-lg"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 mb-3">
                <Mic className="h-8 w-8 text-rose-300" />
              </div>
              <p className="text-sm text-gray-500">No recording submitted yet.</p>
            </div>
          )}
        </div>
      ) : (
        <SelfRecorder
          value={recordingUrl || null}
          maxDurationSec={content.maxDurationSec ?? 60}
          onRecordingUploaded={handleRecordingUploaded}
          onRecordingCleared={handleRecordingCleared}
          getMicConstraints={getMicConstraints}
        />
      )}
      
      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />
      
      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={!recordingUrl || isSubmitting}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto"
          >
            <Send className="h-4 w-4 mr-2" />
            Submit Recording
          </Button>
        </div>
      )}
    </form>
  );
}
